import type { AuthUser, LoginResponse } from '@vmc/shared';

/**
 * In-memory session store. The access token never touches localStorage (XSS would steal it);
 * the long-lived refresh token is an httpOnly cookie the page cannot read.
 */
export interface Session {
  accessToken: string;
  user: AuthUser;
  /** Epoch ms when the access token expires. */
  expiresAt: number;
}

type Listener = () => void;

let current: Session | null = null;
let refreshing: Promise<Session | null> | null = null;
const listeners = new Set<Listener>();

export const getSession = (): Session | null => current;
export const getAccessToken = (): string | null => current?.accessToken ?? null;

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSession(res: LoginResponse | null): void {
  current = res
    ? { accessToken: res.accessToken, user: res.user, expiresAt: Date.now() + res.expiresIn * 1000 }
    : null;
  for (const l of listeners) l();
}

/** Thrown when the refresh endpoint can't be reached, as opposed to rejecting the cookie. */
export class RefreshUnavailableError extends Error {}

/**
 * Exchanges the refresh cookie for a new access token. Concurrent callers share one request,
 * because the server rotates the cookie: two parallel refreshes would race and one would fail.
 * Resolves `null` (and clears the session) when the cookie is rejected; throws
 * `RefreshUnavailableError` on network/5xx errors so a brief API restart doesn't log people out.
 */
export function refreshSession(): Promise<Session | null> {
  refreshing ??= (async () => {
    let res: Response;
    try {
      res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    } catch {
      throw new RefreshUnavailableError('Cannot reach the server');
    }
    if (res.status >= 500) throw new RefreshUnavailableError(`Server error ${res.status}`);
    if (!res.ok) {
      setSession(null);
      return null;
    }
    setSession((await res.json()) as LoginResponse);
    return current;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}
